import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CommentView } from '@thottbot/shared';
import { apiSend, queryKeys, useMe } from '../../api/client';
import { LoginGate } from '../../components/LoginGate';
import { relativeTime } from '../../util/time';
import { VoteArrows } from './VoteArrows';
import { ReportButton } from './ReportButton';
import { describeError } from '../../pages/Submit';

interface Props {
  artifactId: string;
  artifactType: string;
  artifactSlug: string;
  comments: CommentView[];
}

export function Comments({ artifactId, artifactType, artifactSlug, comments }: Props) {
  const qc = useQueryClient();
  const refresh = () =>
    qc.invalidateQueries({ queryKey: queryKeys.artifact(artifactType, artifactSlug) });
  return (
    <div data-testid="comments">
      {comments.length === 0 && <p className="muted">No comments yet.</p>}
      {comments.map((c) => (
        <CommentRow key={c.id} comment={c} onChanged={refresh} />
      ))}
      <LoginGate action="comment">
        <NewComment artifactId={artifactId} onPosted={refresh} />
      </LoginGate>
    </div>
  );
}

function NewComment({ artifactId, onPosted }: { artifactId: string; onPosted: () => void }) {
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      apiSend<CommentView>('POST', `/api/artifacts/${artifactId}/comments`, { body }),
    onSuccess: () => {
      setBody('');
      onPosted();
    },
  });
  return (
    <form
      className="section"
      aria-label="Post a comment"
      onSubmit={(e) => {
        e.preventDefault();
        if (body.trim()) mutation.mutate();
      }}
    >
      <div className="form-row">
        <label htmlFor="new-comment">Add a comment</label>
        <textarea
          id="new-comment"
          value={body}
          maxLength={4000}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What do you know about this?"
        />
      </div>
      {mutation.error && (
        <div className="notice error" role="alert">
          {describeError(mutation.error)}
        </div>
      )}
      <div className="actions">
        <button type="submit" disabled={mutation.isPending || !body.trim()}>
          Post comment
        </button>
      </div>
    </form>
  );
}

function CommentRow({ comment, onChanged }: { comment: CommentView; onChanged: () => void }) {
  const me = useMe();
  const user = me.data?.user ?? null;
  const [open, setOpen] = useState(!comment.collapsed);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [error, setError] = useState<string | null>(null);
  const mine = !!user && user.id === comment.authorId;
  const canRemove = mine || (!!user && user.role !== 'player');

  const save = async () => {
    setError(null);
    try {
      await apiSend('PATCH', `/api/comments/${comment.id}`, { body: draft });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(describeError(err));
    }
  };
  const remove = async () => {
    setError(null);
    try {
      await apiSend('DELETE', `/api/comments/${comment.id}`);
      onChanged();
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <div className={`comment${comment.collapsed ? ' collapsed' : ''}`} data-testid="comment">
      <VoteArrows
        targetType="comment"
        targetId={comment.id}
        score={comment.score}
        myVote={comment.myVote}
      />
      <div className="body">
        <div className="meta">
          <b>{comment.authorBattletag}</b> · {relativeTime(comment.createdAt)}
          {comment.editedAt && ' · edited'}
          {comment.collapsed && (
            <>
              {' · '}
              <button type="button" className="vote-arrow" onClick={() => setOpen(!open)}>
                {open ? 'hide' : 'show'}
              </button>
            </>
          )}
          {mine && !editing && (
            <>
              {' · '}
              <button
                type="button"
                className="vote-arrow"
                style={{ color: 'var(--muted)' }}
                onClick={() => setEditing(true)}
              >
                edit
              </button>
            </>
          )}
          {canRemove && (
            <>
              {' · '}
              <button
                type="button"
                className="vote-arrow"
                style={{ color: 'var(--muted)' }}
                onClick={remove}
              >
                delete
              </button>
            </>
          )}
          {user && !mine && (
            <>
              {' · '}
              <ReportButton targetType="comment" targetId={comment.id} />
            </>
          )}
        </div>
        {editing ? (
          <div>
            <textarea
              aria-label="Edit comment"
              value={draft}
              maxLength={4000}
              onChange={(e) => setDraft(e.target.value)}
              style={{ width: '100%' }}
            />
            <div className="actions">
              <button type="button" onClick={save}>
                Save
              </button>
              <button type="button" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : open ? (
          comment.body
        ) : (
          <span className="muted">Comment hidden (score {comment.score})</span>
        )}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}
